import { memoize } from 'lodash';

import { FeedCategoryNode, ParentsMap } from '../../typings/feedCategories';
import { FeedCategory } from '../../typings/groupsEditState';

export class FeedCategoriesHelpers {
    static findNodeById = memoize(
        (
            id: string | number,
            nodes: Array<FeedCategoryNode>
        ): FeedCategoryNode | undefined => {
            if (!nodes || nodes.length === 0) {
                return undefined;
            }

            let result;
            let node;
            for (let i = 0; !result && i < nodes.length; i++) {
                node = nodes[i];
                if (node.id === id) {
                    return node;
                }

                if (node.children && node.children.length !== 0) {
                    const cache = FeedCategoriesHelpers.findNodeById.cache;
                    const stringId = String(id);

                    if (cache.has(stringId) && !cache.get(stringId)) {
                        cache.delete(stringId);
                    }

                    result = FeedCategoriesHelpers.findNodeById(
                        id,
                        node.children
                    );
                }
            }
            return result;
        },
        id => String(id)
    );

    static areChildrenEmpty(node: FeedCategoryNode): boolean {
        return !node.children || node.children.length === 0;
    }

    static isEveryChildSelected(node: FeedCategoryNode): boolean {
        return FeedCategoriesHelpers.isChildSelected(node, 'every');
    }

    static isAnyChildSelected(node: FeedCategoryNode): boolean {
        return FeedCategoriesHelpers.isChildSelected(node, 'some');
    }

    static selectChildren(
        nodes: Array<FeedCategoryNode>,
        selected: boolean
    ): Array<FeedCategoryNode> {
        return nodes.map(n => ({
            ...n,
            children: n.children
                ? FeedCategoriesHelpers.selectChildren(n.children, selected)
                : [],
            state: {
                ...n.state,
                selected,
                partialSelected: false
            }
        }));
    }

    static selectParents(
        nodes: Array<FeedCategoryNode>
    ): Array<FeedCategoryNode> {
        return nodes.map(node => {
            const isEveryChildSelected = FeedCategoriesHelpers.isEveryChildSelected(
                node
            );

            return {
                ...node,
                state: {
                    ...node.state,
                    selected: isEveryChildSelected,
                    partialSelected:
                        !isEveryChildSelected &&
                        FeedCategoriesHelpers.isAnyChildSelected(node)
                },
                children: node.children
                    ? FeedCategoriesHelpers.selectParents(node.children)
                    : []
            };
        });
    }

    static filterNodes(
        filter: (node: FeedCategoryNode) => boolean,
        nodes: Array<FeedCategoryNode>
    ): Array<FeedCategoryNode> {
        return nodes.reduce((filtered, node: FeedCategoryNode) => {
            const filteredChildren = node.children
                ? FeedCategoriesHelpers.filterNodes(filter, node.children)
                : [];

            return !filter(node)
                ? [...filtered, ...filteredChildren]
                : [
                      ...filtered,
                      {
                          ...node,
                          children: filteredChildren
                      }
                  ];
        }, []);
    }

    static isSelected(node: FeedCategoryNode): boolean {
        return (
            node.state && (node.state.selected || node.state.partialSelected)
        );
    }

    static isMatched(search: string): (node: FeedCategoryNode) => boolean {
        return (node: FeedCategoryNode): boolean => {
            const upperCaseId = node.id.toUpperCase();
            const upperCaseName = node.name.toUpperCase();
            const upperCaseSearchTerm = search.toUpperCase().trim();

            return (
                upperCaseName.indexOf(upperCaseSearchTerm) > -1 ||
                upperCaseId.indexOf(upperCaseSearchTerm) > -1
            );
        };
    }

    static isRelevant(node: FeedCategoryNode): boolean {
        return !node.state.deleted;
    }

    static selectNodes = (
        nodes: Array<FeedCategoryNode>,
        updatedNodes: Array<FeedCategoryNode>
    ): Array<FeedCategoryNode> => {
        return nodes.map(node => {
            const updatedNode = updatedNodes.find(({ id }) => id === node.id);
            if (updatedNode) {
                return {
                    ...updatedNode,
                    state: {
                        ...updatedNode.state,
                        partialSelected: false
                    },
                    children: node.children
                        ? FeedCategoriesHelpers.selectChildren(
                              node.children,
                              updatedNode.state && updatedNode.state.selected
                          )
                        : []
                };
            }

            if (node.children) {
                return {
                    ...node,
                    children: FeedCategoriesHelpers.selectNodes(
                        node.children,
                        updatedNodes
                    )
                };
            }

            return node;
        });
    };

    static generateParentsMap(nodes: Array<FeedCategoryNode>): ParentsMap {
        const map = new Map();

        const fillMap = (
            innerNodes: Array<FeedCategoryNode>,
            parents: Array<string | number> = []
        ): void => {
            innerNodes.forEach(node => {
                if (node.children) {
                    fillMap(node.children, [...parents, node.id]);
                }

                map.set(node.id, parents);
            });
        };

        fillMap(nodes);

        return map;
    }

    static generateTree = (
        nodes: Array<FeedCategory>,
        selectedIds: Array<string> = [],
        parent = { id: '-' }
    ): Array<FeedCategoryNode> => {
        const children: Array<FeedCategoryNode> = nodes
            .filter(child => String(child.parentCategoryId) === parent.id)
            .map(child => ({
                id: String(child.categoryId),
                name: child.name,
                state: {
                    expanded: true,
                    deleted: child.isDeleted,
                    selected: selectedIds.includes(child.categoryId),
                    partialSelected: false
                }
            }));

        return children.map(child => {
            const nodeChildren = FeedCategoriesHelpers.generateTree(
                nodes,
                selectedIds,
                child
            );

            if (nodeChildren.length !== 0) {
                child.children = nodeChildren;
            }

            return child;
        });
    };

    static sortDeleted = (
        categories: Array<FeedCategory>
    ): Array<FeedCategory> => {
        return categories.sort((a, b) => {
            if (a.isDeleted && !b.isDeleted) {
                return -1;
            }
            if (!a.isDeleted && b.isDeleted) {
                return 1;
            }
            return 0;
        });
    };

    static changeDeletedCategoriesParent = (
        categories: Array<FeedCategory>
    ): Array<FeedCategory> => {
        return categories.map(category => {
            const parent = categories.find(
                ({ categoryId }) => categoryId === category.parentCategoryId
            );

            const isParentDeleted = !parent || parent.isDeleted;
            const isCategoryDeleted = category.isDeleted;

            return {
                ...category,
                parentCategoryId:
                    isParentDeleted || isCategoryDeleted
                        ? '-'
                        : category.parentCategoryId
            };
        });
    };

    static getFlattenedTree(
        tree: Array<FeedCategoryNode>
    ): Array<FeedCategoryNode> {
        return tree.reduce((flattenedTree, node) => {
            if (node.children && node.children.length !== 0) {
                return [
                    ...flattenedTree,
                    node,
                    ...FeedCategoriesHelpers.getFlattenedTree(node.children)
                ];
            }

            return [...flattenedTree, node];
        }, []);
    }

    private static isChildSelected(
        node: FeedCategoryNode,
        method: 'every' | 'some'
    ): boolean {
        if (FeedCategoriesHelpers.areChildrenEmpty(node)) {
            return node.state && node.state.selected;
        }

        if (node.children) {
            return node.children[method](child => {
                if (!FeedCategoriesHelpers.areChildrenEmpty(child)) {
                    return FeedCategoriesHelpers.isChildSelected(child, method);
                }

                return child.state && child.state.selected;
            });
        }

        return false;
    }
}
